import { useMemo } from "react";
import { useFormStore } from "../store/formStore";
import { isRequiredDocsVisible, isOwnerEligibilityVisible, isPriorBillVisible } from "./step7Rules";

export interface Step7Visibility {
  showRequiredDocuments: boolean;
  showPriorBill: boolean;
  showOwnerEligibility: boolean;
  showNewlyFormedToggle: boolean;
  showNewlyFormedDocs: boolean;
}

/**
 * Encapsulates cross-step visibility logic for the Documents & Employees page.
 * Reads from the Zustand store and returns memoized visibility flags.
 */
export function useStep7Visibility(): Step7Visibility {
  const replacingMedical = useFormStore((s) => s.data.replacingMedical);
  const hasEligibleOwner = useFormStore((s) => isOwnerEligibilityVisible(s.data.ownershipAssignments));
  const docVerificationType = useFormStore((s) => s.data.docVerificationType);
  const newlyFormedCompany = useFormStore((s) => s.data.newlyFormedCompany);
  const totalEnrolledEmployees = useFormStore((s) => s.data.totalEnrolledEmployees);

  return useMemo(() => {
    // Required Documents: hidden when BOTH totalEnrolled >= 20 AND replacing medical
    const showRequiredDocuments = isRequiredDocsVisible(totalEnrolledEmployees, replacingMedical);

    // Prior Carrier Bill: visible when replacing existing medical coverage
    const showPriorBill = isPriorBillVisible({ replacingMedical });

    // Owner Eligibility: visible when ANY owner is enrolling but NOT receiving W-2 wages
    const showOwnerEligibility = hasEligibleOwner;

    // Newly formed company toggle: only when Owner Eligibility is visible but Required Documents is hidden
    const showNewlyFormedToggle = showOwnerEligibility && !showRequiredDocuments;

    // Newly formed company doc list overrides org-type docs when:
    // - Required Docs visible: "Employment verification document" = "Business in operation less than 90 days"
    // - Required Docs hidden: "Was this company established within the last 90 days?" = Yes
    const showNewlyFormedDocs = showOwnerEligibility && (
      (showRequiredDocuments && docVerificationType === "Business in operation less than 90 days") ||
      (!showRequiredDocuments && newlyFormedCompany === "Yes")
    );

    return {
      showRequiredDocuments,
      showPriorBill,
      showOwnerEligibility,
      showNewlyFormedToggle,
      showNewlyFormedDocs,
    };
  }, [replacingMedical, hasEligibleOwner, docVerificationType, newlyFormedCompany, totalEnrolledEmployees]);
}
